import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { providerService, providerCategoryService } from '../../services/providerService';
import { useAuth } from '../../contexts/AuthContext';

const PAYMENT_TERMS = [
  { value: 'contado', label: 'Contado' },
  { value: 'credito_15', label: 'Crédito 15 días' },
  { value: 'credito_30', label: 'Crédito 30 días' },
  { value: 'credito_60', label: 'Crédito 60 días' },
];

const emptyProvider = {
  name: '', categoryId: '', phone: '', whatsapp: '', email: '',
  address: '', city: '', contactName: '', schedule: '', paymentTerms: 'contado', notes: ''
};

const emptyCategory = { name: '' };

const ProvidersManagement = () => {
  const { userProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('providers');

  // Providers state
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Form state
  const [showProviderForm, setShowProviderForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [providerForm, setProviderForm] = useState(emptyProvider);
  const [savingProvider, setSavingProvider] = useState(false);

  // Category form
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [savingCategory, setSavingCategory] = useState(false);

  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState(null);

  // Delete category state
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState(null); // { id, name, providerCount }
  const [deletingCategory, setDeletingCategory] = useState(false);

  const companyId = userProfile?.companyId;

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cats, provs] = await Promise.all([
        providerCategoryService?.getAll(companyId),
        providerService?.getAll(companyId)
      ]);
      setCategories(cats || []);
      setProviders(provs || []);
    } catch (err) {
      console.error('[ProvidersManagement] Load error:', err);
      setError('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveProvider = async () => {
    if (!providerForm?.name?.trim()) { alert('El nombre es obligatorio'); return; }
    setSavingProvider(true);
    try {
      const payload = { ...providerForm, companyId: companyId || null };
      if (editingProvider) {
        await providerService?.update(editingProvider?.id, payload);
        showSuccess('Proveedor actualizado');
      } else {
        await providerService?.create(payload);
        showSuccess('Proveedor creado exitosamente');
      }
      setShowProviderForm(false);
      setEditingProvider(null);
      setProviderForm(emptyProvider);
      loadData();
    } catch (err) {
      alert('Error al guardar proveedor: ' + (err?.message || ''));
    } finally {
      setSavingProvider(false);
    }
  };

  const handleEditProvider = (provider) => {
    setEditingProvider(provider);
    setProviderForm({
      name: provider?.name || '',
      categoryId: provider?.categoryId || '',
      phone: provider?.phone || '',
      whatsapp: provider?.whatsapp || '',
      email: provider?.email || '',
      address: provider?.address || '',
      city: provider?.city || '',
      contactName: provider?.contactName || '',
      schedule: provider?.schedule || '',
      paymentTerms: provider?.paymentTerms || 'contado',
      notes: provider?.notes || ''
    });
    setShowProviderForm(true);
  };

  const handleDeleteProvider = async (id) => {
    if (!window.confirm('¿Desactivar este proveedor?')) return;
    try {
      await providerService?.delete(id);
      showSuccess('Proveedor desactivado');
      loadData();
    } catch (err) {
      alert('Error: ' + (err?.message || ''));
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm?.name?.trim()) { alert('El nombre es obligatorio'); return; }
    setSavingCategory(true);
    try {
      await providerCategoryService?.create({ ...categoryForm, companyId: companyId || null });
      showSuccess('Categoría creada');
      setShowCategoryForm(false);
      setCategoryForm(emptyCategory);
      loadData();
    } catch (err) {
      alert('Error al crear categoría: ' + (err?.message || ''));
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategoryRequest = (cat) => {
    const count = providers?.filter(p => p?.categoryId === cat?.id)?.length || 0;
    setDeleteCategoryTarget({ id: cat?.id, name: cat?.name, providerCount: count });
  };

  const handleConfirmDeleteCategory = async () => {
    if (!deleteCategoryTarget) return;
    setDeletingCategory(true);
    try {
      await providerCategoryService?.delete(deleteCategoryTarget?.id);
      showSuccess(`Categoría "${deleteCategoryTarget?.name}" eliminada`);
      setDeleteCategoryTarget(null);
      loadData();
    } catch (err) {
      alert('Error al eliminar categoría: ' + (err?.message || ''));
    } finally {
      setDeletingCategory(false);
    }
  };

  const filteredProviders = providers?.filter(p => {
    const matchSearch = !searchTerm || p?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) || p?.city?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchCat = !filterCategory || p?.categoryId === filterCategory;
    return matchSearch && matchCat;
  });

  return (
    <>
      <Helmet>
        <title>Proveedores - Gearcontrol</title>
        <meta name="description" content="Gestión de proveedores de refacciones, llantas y servicios" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <MainSidebar isCollapsed={sidebarCollapsed} userRole="admin" />
        <div className={`transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-1">
            <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth press-effect focus-ring flex-shrink-0">
                  <Icon name={sidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
                </button>
                <div className="min-w-0 pl-10 lg:pl-0">
                  <h1 className="text-xl font-heading font-bold text-foreground leading-tight">Proveedores</h1>
                  <p className="text-sm caption text-muted-foreground mt-1">Gestión de proveedores y categorías</p>
                </div>
              </div>
              <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => { setEditingProvider(null); setProviderForm(emptyProvider); setShowProviderForm(true); }} className="flex-shrink-0">
                <span className="hidden sm:inline">Nuevo Proveedor</span>
                <span className="sm:hidden sr-only">Nuevo</span>
              </Button>
            </div>
          </header>

          <main className="px-4 md:px-6 lg:px-8 py-6 space-y-6">
            {successMsg && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <p className="text-sm text-success">{successMsg}</p>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border">
              {[{ id: 'providers', label: 'Proveedores', icon: 'Building2' }, { id: 'categories', label: 'Categorías', icon: 'Tag' }]?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-smooth ${
                    activeTab === tab?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* PROVIDERS TAB */}
            {activeTab === 'providers' && (
              <>
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Input
                      placeholder="Buscar por nombre o ciudad..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      iconName="Search"
                    />
                  </div>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e?.target?.value)}
                    className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Todas las categorías</option>
                    {categories?.map(cat => <option key={cat?.id} value={cat?.id}>{cat?.name}</option>)}
                  </select>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <Icon name="Loader2" size={32} className="text-primary animate-spin" />
                  </div>
                ) : filteredProviders?.length === 0 ? (
                  <div className="text-center py-16 bg-card rounded-lg border border-border">
                    <Icon name="Building2" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground text-lg">No hay proveedores registrados</p>
                    <p className="text-sm text-muted-foreground mt-1">Agrega tu primer proveedor para comenzar</p>
                    <Button variant="default" iconName="Plus" iconPosition="left" className="mt-4" onClick={() => { setEditingProvider(null); setProviderForm(emptyProvider); setShowProviderForm(true); }}>
                      Agregar Proveedor
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProviders?.map(provider => (
                      <div key={provider?.id} className="bg-card rounded-lg border border-border p-5 hover:border-primary/50 transition-smooth">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <Icon name="Building2" size={20} className="text-primary" />
                            </div>
                            <div>
                              <h3 className="font-heading font-semibold text-foreground">{provider?.name}</h3>
                              <span className="text-xs text-muted-foreground">{provider?.category?.name || 'Sin categoría'}</span>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => handleEditProvider(provider)} className="p-1.5 rounded hover:bg-muted transition-smooth">
                              <Icon name="Pencil" size={15} className="text-muted-foreground" />
                            </button>
                            <button onClick={() => handleDeleteProvider(provider?.id)} className="p-1.5 rounded hover:bg-destructive/10 transition-smooth">
                              <Icon name="Trash2" size={15} className="text-destructive" />
                            </button>
                          </div>
                        </div>
                        <div className="space-y-1.5 text-sm">
                          {provider?.phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Icon name="Phone" size={14} />
                              <span>{provider?.phone}</span>
                            </div>
                          )}
                          {provider?.whatsapp && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Icon name="MessageCircle" size={14} />
                              <span>{provider?.whatsapp}</span>
                            </div>
                          )}
                          {provider?.email && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Icon name="Mail" size={14} />
                              <span>{provider?.email}</span>
                            </div>
                          )}
                          {provider?.city && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Icon name="MapPin" size={14} />
                              <span>{provider?.city}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Icon name="CreditCard" size={14} />
                            <span>{PAYMENT_TERMS?.find(t => t?.value === provider?.paymentTerms)?.label || provider?.paymentTerms}</span>
                          </div>
                        </div>
                        {provider?.notes && (
                          <p className="mt-3 text-xs text-muted-foreground bg-muted/50 rounded p-2">{provider?.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* CATEGORIES TAB */}
            {activeTab === 'categories' && (
              <>
                <div className="flex justify-end">
                  <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowCategoryForm(true)}>
                    Nueva Categoría
                  </Button>
                </div>
                {showCategoryForm && (
                  <div className="bg-card rounded-lg border border-primary/30 p-6">
                    <h3 className="text-base font-heading font-semibold mb-4">Nueva Categoría</h3>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          label="Nombre de la categoría"
                          placeholder="Ej: Refaccionarías, Llanteras..."
                          value={categoryForm?.name}
                          onChange={(e) => setCategoryForm(p => ({ ...p, name: e?.target?.value }))}
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <Button variant="outline" onClick={() => setShowCategoryForm(false)}>Cancelar</Button>
                        <Button variant="default" onClick={handleSaveCategory} disabled={savingCategory} loading={savingCategory}>Guardar</Button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories?.map(cat => {
                    const provCount = providers?.filter(p => p?.categoryId === cat?.id)?.length || 0;
                    return (
                      <div key={cat?.id} className="bg-card rounded-lg border border-border p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-accent/10">
                            <Icon name="Tag" size={18} className="text-accent" />
                          </div>
                          <div>
                            <span className="font-medium text-foreground">{cat?.name}</span>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {provCount === 0 ? 'Sin proveedores' : `${provCount} proveedor${provCount !== 1 ? 'es' : ''}`}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteCategoryRequest(cat)}
                          className="p-1.5 rounded hover:bg-destructive/10 transition-smooth"
                          title="Eliminar categoría"
                        >
                          <Icon name="Trash2" size={15} className="text-destructive" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
      {/* Provider Form Modal */}
      {showProviderForm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border border-border shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-xl font-heading font-semibold">{editingProvider ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h2>
              <button onClick={() => { setShowProviderForm(false); setEditingProvider(null); setProviderForm(emptyProvider); }} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Input label="Nombre del Proveedor *" placeholder="Ej: Autopartes del Norte" value={providerForm?.name} onChange={(e) => setProviderForm(p => ({ ...p, name: e?.target?.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Categoría</label>
                  <select value={providerForm?.categoryId} onChange={(e) => setProviderForm(p => ({ ...p, categoryId: e?.target?.value }))} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Sin categoría</option>
                    {categories?.map(cat => <option key={cat?.id} value={cat?.id}>{cat?.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Condiciones de Pago</label>
                  <select value={providerForm?.paymentTerms} onChange={(e) => setProviderForm(p => ({ ...p, paymentTerms: e?.target?.value }))} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    {PAYMENT_TERMS?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
                  </select>
                </div>
                <Input label="Teléfono" placeholder="10 dígitos" value={providerForm?.phone} onChange={(e) => setProviderForm(p => ({ ...p, phone: e?.target?.value }))} />
                <Input label="WhatsApp" placeholder="10 dígitos" value={providerForm?.whatsapp} onChange={(e) => setProviderForm(p => ({ ...p, whatsapp: e?.target?.value }))} />
                <Input label="Email" type="email" placeholder="proveedor@email.com" value={providerForm?.email} onChange={(e) => setProviderForm(p => ({ ...p, email: e?.target?.value }))} />
                <Input label="Contacto" placeholder="Nombre del contacto" value={providerForm?.contactName} onChange={(e) => setProviderForm(p => ({ ...p, contactName: e?.target?.value }))} />
                <Input label="Ciudad" placeholder="Ciudad" value={providerForm?.city} onChange={(e) => setProviderForm(p => ({ ...p, city: e?.target?.value }))} />
                <Input label="Horario" placeholder="Lun-Vie 9am-6pm" value={providerForm?.schedule} onChange={(e) => setProviderForm(p => ({ ...p, schedule: e?.target?.value }))} />
                <div className="md:col-span-2">
                  <Input label="Dirección" placeholder="Calle, número, colonia" value={providerForm?.address} onChange={(e) => setProviderForm(p => ({ ...p, address: e?.target?.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">Notas</label>
                  <textarea value={providerForm?.notes} onChange={(e) => setProviderForm(p => ({ ...p, notes: e?.target?.value }))} rows={3} placeholder="Condiciones especiales, observaciones..." className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-border">
              <Button variant="outline" onClick={() => { setShowProviderForm(false); setEditingProvider(null); setProviderForm(emptyProvider); }}>Cancelar</Button>
              <Button variant="default" iconName="Save" iconPosition="left" onClick={handleSaveProvider} disabled={savingProvider} loading={savingProvider}>
                {editingProvider ? 'Actualizar' : 'Guardar Proveedor'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Confirmation Modal */}
      {deleteCategoryTarget && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl border border-border shadow-elevation-3 w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-destructive/10">
                  <Icon name="Trash2" size={20} className="text-destructive" />
                </div>
                <h2 className="text-lg font-heading font-semibold text-foreground">Eliminar categoría</h2>
              </div>
              <button onClick={() => setDeleteCategoryTarget(null)} className="p-2 rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {deleteCategoryTarget?.providerCount > 0 ? (
                <>
                  <div className="flex items-start gap-3 bg-warning/10 border border-warning/30 rounded-lg p-4">
                    <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">No se puede eliminar esta categoría</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        La categoría <span className="font-semibold text-foreground">"{deleteCategoryTarget?.name}"</span> tiene{' '}
                        <span className="font-semibold text-foreground">{deleteCategoryTarget?.providerCount} proveedor{deleteCategoryTarget?.providerCount !== 1 ? 'es' : ''}</span> asociado{deleteCategoryTarget?.providerCount !== 1 ? 's' : ''}.
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Para eliminarla, primero reasigna o quita la categoría de esos proveedores desde la pestaña <span className="font-medium text-foreground">Proveedores</span>.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setDeleteCategoryTarget(null)}>Entendido</Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    ¿Estás seguro de que deseas eliminar la categoría{' '}
                    <span className="font-semibold text-foreground">"{deleteCategoryTarget?.name}"</span>?
                    Esta acción no se puede deshacer.
                  </p>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setDeleteCategoryTarget(null)}>Cancelar</Button>
                    <Button
                      variant="destructive"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={handleConfirmDeleteCategory}
                      disabled={deletingCategory}
                      loading={deletingCategory}
                    >
                      Eliminar categoría
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProvidersManagement;
